import { Component } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ProductService } from 'src/app/product/product.service';

function validateProductCode(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value || '').trim();
  const id = Number(value);
  return /^\d+$/.test(value) && Number.isInteger(id) && id > 0 && id <= 2147483647
    ? null
    : { productCode: true };
}

@Component({
  selector: 'app-product-code-search-dialog',
  template: `
    <form (ngSubmit)="search()">
      <div class="titleRow">
        <h2 mat-dialog-title>Поиск по коду товара</h2>
        <button mat-icon-button mat-dialog-close type="button" aria-label="Закрыть">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <mat-dialog-content>
        <p class="description">Введите код товара, указанный на сайте.</p>
        <mat-form-field appearance="outline">
          <mat-label>Код товара</mat-label>
          <input
            matInput
            type="text"
            inputmode="numeric"
            autocomplete="off"
            placeholder="Например, 968"
            [formControl]="codeControl"
            [readonly]="loading"
            (input)="error = null"
          />
          <mat-error>Введите целый положительный код товара</mat-error>
        </mat-form-field>
        <p *ngIf="error" class="error" role="alert">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close type="button">Отмена</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="loading">
          {{ loading ? 'Поиск…' : 'Найти товар' }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [`
    :host { display: block; font-size: 15px; font-weight: 400; }
    .titleRow { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    h2 { margin: 0; font-size: 20px; font-weight: 400; }
    .description { color: #64748b; margin: 12px 0 20px; }
    mat-form-field { width: 100%; font-size: 15px; }
    button { font-size: 15px; font-weight: 400; }
    .error { color: #b91c1c; margin: 0 0 12px; }
  `]
})
export class ProductCodeSearchDialogComponent {
  readonly codeControl = new FormControl('', {
    nonNullable: true,
    validators: validateProductCode
  });
  loading = false;
  error: string | null = null;

  constructor(
    private readonly productService: ProductService,
    private readonly dialogRef: MatDialogRef<ProductCodeSearchDialogComponent, number>
  ) {}

  async search(): Promise<void> {
    if (this.loading) {
      return;
    }
    if (this.codeControl.invalid) {
      this.codeControl.markAsTouched();
      return;
    }
    const id = Number(this.codeControl.value.trim());
    this.loading = true;
    this.error = null;
    try {
      const product = await firstValueFrom(this.productService.getOne(id));
      if (product?.id) {
        // Старый код объединённого товара может возвращать ID основной карточки.
        this.dialogRef.close(product.id);
      } else {
        this.error = `Товар с кодом ${id} не найден`;
      }
    } catch (error) {
      this.error = (error as { status?: number })?.status === 404
        ? `Товар с кодом ${id} не найден`
        : 'Не удалось найти товар. Попробуйте ещё раз.';
    } finally {
      this.loading = false;
    }
  }
}
